import type {
  PositionAvailabilityMachineState,
  positionAvailabilityWorkflow
} from '../state-machine/positionAvailabilityMachine';
import { useCartActor } from '../CartProvider';
import { POSITION_AVAILABILITY_OPTIONS_ACTOR_ID } from '../state-machine/cartMachine';
import type { ActorRefFrom } from 'xstate';
import { useSelector } from '@xstate/react';
import { useCartStateSelector } from './useCartStateSelector';

export type CartBusyDialogDescriptor =
  | 'COPY_ORDER'
  | 'COPY_TEMPLATE'
  | 'COPY_SALESORDERSET';

function usePositionAvailabilitySelector<T>(selector: (state: PositionAvailabilityMachineState) => T) {
  const cartActorRef = useCartActor();
  const actorRef = cartActorRef?.getSnapshot().children[POSITION_AVAILABILITY_OPTIONS_ACTOR_ID] as ActorRefFrom<
    typeof positionAvailabilityWorkflow
  >;

  return useSelector(actorRef, selector);
}

export function useCartIsBusy() {
  const cartIsCancelling = useCartStateSelector<boolean>(state => state?.value === 'CancellingOrder');
  const cartIsSendingOrder = useCartStateSelector<boolean>(state => state?.value === 'SendingOrder');
  const cartIsChangingDeliveryDate = useCartStateSelector<boolean>(state =>
    state?.matches({ ChangingDeliveryDateOfCart: 'ProcessingChangeDeliveryDateOfCart' })
  );

  const cartIsChangingDeliveryDateExpired = useCartStateSelector<boolean>(state =>
    state?.matches({ DeliveryDateExpired: 'ProcessingChangeDeliveryDateOfCart' })
  );

  const cartIsProcessingPositionIntent = useCartStateSelector<boolean>(
    state =>
      state?.matches({ ProcessingPositions: 'ProcessingPositionIntent' }) ||
      state?.matches({ ProcessingPositions: 'CheckingCartStatus' }) ||
      state?.matches({ ProcessingPositions: 'RefetchingOrder' })
  );

  const cartIsProcessingAvailabilityOption = usePositionAvailabilitySelector<boolean>(state => {
    return state?.value === 'ProcessingPositionAvailabilityOption';
  });

  const cartIsRefreshing = useCartStateSelector<boolean>(
    state => state?.matches({ Idle: 'IdleReloading' }) || state?.matches('CartRefreshing')
  );

  const cartIsProcessingPosition = cartIsProcessingPositionIntent || cartIsProcessingAvailabilityOption;

  const copyingOrderToCartIsScheduled = useCartStateSelector<boolean>(
    state =>
      state?.context?.positionIntents?.some(
        positionIntent => positionIntent.operation === 'COPY_EXISTING_ORDER_TO_CART'
      ) &&
      !state?.matches({ ProcessingPositions: 'CheckingCartStatus' }) &&
      !state?.matches({ ProcessingPositions: 'RefetchingOrder' })
  );
  const copyingOrderToCartIsInProgress = useCartStateSelector<boolean>(state => {
    const scheduledPositionIntent = state?.context?.scheduledPositionIntent;
    return scheduledPositionIntent?.operation === 'COPY_EXISTING_ORDER_TO_CART';
  });

  const copyingTemplateArticleToCartIsScheduled = useCartStateSelector<boolean>(
    state =>
      state?.context?.positionIntents?.some(
        positionIntent => positionIntent.operation === 'COPY_TEMPLATE_ARTICLE_TO_CART'
      ) &&
      !state?.matches({ ProcessingPositions: 'CheckingCartStatus' }) &&
      !state?.matches({ ProcessingPositions: 'RefetchingOrder' })
  );
  const copyingTemplateArticleToCartIsInProgress = useCartStateSelector<boolean>(state => {
    const scheduledPositionIntent = state?.context?.scheduledPositionIntent;
    return scheduledPositionIntent?.operation === 'COPY_TEMPLATE_ARTICLE_TO_CART';
  });

  const copyingSalesOrderSetToCartIsScheduled = useCartStateSelector<boolean>(
    state =>
      state?.context?.positionIntents?.some(
        positionIntent => positionIntent.operation === 'COPY_SALESORDERSET_TO_CART'
      ) &&
      !state?.matches({ ProcessingPositions: 'CheckingCartStatus' }) &&
      !state?.matches({ ProcessingPositions: 'RefetchingOrder' })
  );
  const copyingSalesOrderSetToCartIsInProgress = useCartStateSelector<boolean>(state => {
    const scheduledPositionIntent = state?.context?.scheduledPositionIntent;
    return scheduledPositionIntent?.operation === 'COPY_SALESORDERSET_TO_CART';
  });

  const importingCsvToCartIsScheduled = useCartStateSelector<boolean>(
    state =>
      state?.context?.positionIntents?.some(positionIntent => positionIntent.operation === 'IMPORT_CSV_TO_CART') &&
      !state?.matches({ ProcessingPositions: 'CheckingCartStatus' }) &&
      !state?.matches({ ProcessingPositions: 'RefetchingOrder' })
  );

  const importingCsvToCartIsInProgress = useCartStateSelector<boolean>(state => {
    const scheduledPositionIntent = state?.context?.scheduledPositionIntent;
    return scheduledPositionIntent?.operation === 'IMPORT_CSV_TO_CART';
  });
  const isCopyingOrderToCart = copyingOrderToCartIsScheduled || copyingOrderToCartIsInProgress;
  const isCopyingTemplateArticleToCart =
    copyingTemplateArticleToCartIsScheduled || copyingTemplateArticleToCartIsInProgress;
  const isCopyingSalesOrderSetToCart = copyingSalesOrderSetToCartIsScheduled || copyingSalesOrderSetToCartIsInProgress;
  const cartIsImportingCsv = importingCsvToCartIsScheduled || importingCsvToCartIsInProgress;
  
  let busyDialogContentDescriptor: CartBusyDialogDescriptor | null = null;

  if (isCopyingOrderToCart) {
    busyDialogContentDescriptor = 'COPY_ORDER';
  } else if (isCopyingSalesOrderSetToCart) {
    busyDialogContentDescriptor = 'COPY_SALESORDERSET';
  } else if (isCopyingTemplateArticleToCart) {
    busyDialogContentDescriptor = 'COPY_TEMPLATE';
  }

  return {
    busyDialogContentDescriptor,
    cartIsProcessingPosition,
    cartIsRefreshing,
    cartIsCancelling,
    cartIsSendingOrder,
    cartIsChangingDeliveryDate,
    cartIsChangingDeliveryDateExpired,
    cartIsImportingCsv,
  };
}
